import { DISCORD_SNOWFLAKE } from "./discord.ts";
import { carriesDirectionControl, showsText } from "./names.ts";
import { z } from "zod";

export const LEVEL_UP_PLACEHOLDERS = [
  "user",
  "username",
  "level",
  "previousLevel",
  "prestige",
  "xp",
  "server",
  "avatar",
] as const;

export type LevelUpPlaceholder = (typeof LEVEL_UP_PLACEHOLDERS)[number];

export type LevelUpValues = Readonly<Record<LevelUpPlaceholder, string>>;

export const RANK_REPLY_PLACEHOLDERS = [
  "user",
  "username",
  "level",
  "prestige",
  "xp",
  "rank",
  "server",
  "avatar",
] as const;

export type RankReplyPlaceholder = (typeof RANK_REPLY_PLACEHOLDERS)[number];

export type RankReplyValues = Readonly<Record<RankReplyPlaceholder, string>>;

export type MessageValues = Readonly<Record<string, string>> & {
  readonly user: string;
  readonly avatar: string;
};

export const DISCORD_MESSAGE_LIMITS = {
  content: 2_000,
  embeds: 10,
  embedTitle: 256,
  embedDescription: 4_096,
  embedFields: 25,
  fieldName: 256,
  fieldValue: 1_024,
  footerText: 2_048,
  authorName: 256,
  embedTotal: 6_000,
  actionRows: 5,
  rowButtons: 5,
  buttonLabel: 80,
  buttonUrl: 512,
  components: 40,
  componentText: 4_000,
  sectionTexts: 3,
  galleryItems: 10,
  mediaDescription: 1_024,
  attachments: 10,
} as const;

const LIMIT = DISCORD_MESSAGE_LIMITS;

export const MAX_TEMPLATE_LENGTH = LIMIT.content;

export const COMPONENTS_V2_FLAG = 32_768;

const DISCORD_COMPONENT_TYPES = {
  row: 1,
  button: 2,
  stringSelect: 3,
  section: 9,
  text: 10,
  thumbnail: 11,
  gallery: 12,
  file: 13,
  separator: 14,
  container: 17,
} as const;

export const LINK_BUTTON_STYLE = 5;

const SEPARATOR_SPACINGS = ["small", "large"] as const;

type SeparatorSpacing = (typeof SEPARATOR_SPACINGS)[number];

const SPACING_VALUE: Readonly<Record<SeparatorSpacing, 1 | 2>> = { small: 1, large: 2 };

export const TEMPLATE_REFUSALS = [
  "empty_message",
  "empty_embed",
  "embed_author",
  "embed_footer",
  "embed_total",
  "too_many_components",
  "component_text",
  "too_many_pictures",
] as const;

export type TemplateRefusal = (typeof TEMPLATE_REFUSALS)[number];

const refusal = (reason: TemplateRefusal, path: readonly (string | number)[] = []) => ({
  message: reason.replaceAll("_", " "),
  params: { reason },
  path: [...path],
});

export const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, "is not a #rrggbb colour");

const hexColor = hexColorSchema;

const link = (max: number) => z.url({ protocol: /^https?$/, hostname: z.regexes.domain }).max(max);

const optionalText = (max: number) => z.string().max(max);

const optionalLink = (max: number) => z.union([z.literal(""), link(max)]);

const MEDIA_URL_MAX = 2_048;

export const STORED_PICTURE_PREFIX = "asset:";

const storedPictureSchema = z.templateLiteral([STORED_PICTURE_PREFIX, z.uuid()]);

export const storedPicture = (assetId: string): `${typeof STORED_PICTURE_PREFIX}${string}` =>
  `${STORED_PICTURE_PREFIX}${assetId}`;

export const storedPictureId = (url: string): string | null =>
  storedPictureSchema.safeParse(url).success ? url.slice(STORED_PICTURE_PREFIX.length) : null;

export const AVATAR_PICTURE = "{avatar}";

export const CARD_PICTURE = "{card}";

export const RANK_CARD_FILE_NAME = "colossus-rank.png";

const iconSources = [link(MEDIA_URL_MAX), storedPictureSchema, z.literal(AVATAR_PICTURE)] as const;

const pictureSources = [...iconSources, z.literal(CARD_PICTURE)] as const;

const pictureSchema = z.union(pictureSources);

const optionalPicture = z.union([z.literal(""), ...pictureSources]);

const optionalIcon = z.union([z.literal(""), ...iconSources]);

const linkButtonSchema = z.strictObject({
  label: z.string().min(1).max(LIMIT.buttonLabel),
  url: link(LIMIT.buttonUrl),
});

type LinkButton = z.infer<typeof linkButtonSchema>;

const buttonsSchema = z.array(linkButtonSchema).min(1).max(LIMIT.rowButtons);

const embedFieldSchema = z.strictObject({
  name: z.string().min(1).max(LIMIT.fieldName),
  value: z.string().min(1).max(LIMIT.fieldValue),
  inline: z.boolean(),
});

const embedSchema = z
  .strictObject({
    color: hexColor.nullable(),
    author: z.strictObject({
      name: optionalText(LIMIT.authorName),
      url: optionalLink(MEDIA_URL_MAX),
      iconUrl: optionalIcon,
    }),
    title: optionalText(LIMIT.embedTitle),
    url: optionalLink(MEDIA_URL_MAX),
    description: optionalText(LIMIT.embedDescription),
    fields: z.array(embedFieldSchema).max(LIMIT.embedFields),
    thumbnailUrl: optionalPicture,
    imageUrl: optionalPicture,
    footer: z.strictObject({
      text: optionalText(LIMIT.footerText),
      iconUrl: optionalIcon,
    }),
    timestamp: z.boolean(),
  })
  .refine(
    (embed) =>
      embed.title !== "" ||
      embed.description !== "" ||
      embed.fields.length > 0 ||
      embed.author.name !== "" ||
      embed.footer.text !== "" ||
      embed.thumbnailUrl !== "" ||
      embed.imageUrl !== "",
    refusal("empty_embed"),
  )
  .refine(
    (embed) => embed.author.name !== "" || (embed.author.url === "" && embed.author.iconUrl === ""),
    refusal("embed_author", ["author", "name"]),
  )
  .refine(
    (embed) => embed.footer.text !== "" || embed.footer.iconUrl === "",
    refusal("embed_footer", ["footer", "text"]),
  );

export type Embed = z.infer<typeof embedSchema>;

const actionRowSchema = z.strictObject({ buttons: buttonsSchema });

const media = z.strictObject({
  url: pictureSchema,
  description: optionalText(LIMIT.mediaDescription),
  spoiler: z.boolean(),
});

type MediaItem = z.infer<typeof media>;

const componentText = z.string().min(1).max(LIMIT.componentText);

const textDisplay = z.strictObject({ type: z.literal("text"), content: componentText });

const thumbnailAccessory = media.extend({ type: z.literal("thumbnail") });

const buttonAccessory = linkButtonSchema.extend({ type: z.literal("button") });

const section = z.strictObject({
  type: z.literal("section"),
  texts: z.array(componentText).min(1).max(LIMIT.sectionTexts),
  accessory: z.discriminatedUnion("type", [thumbnailAccessory, buttonAccessory]),
});

const gallery = z.strictObject({
  type: z.literal("gallery"),
  items: z.array(media).min(1).max(LIMIT.galleryItems),
});

const separator = z.strictObject({
  type: z.literal("separator"),
  divider: z.boolean(),
  spacing: z.enum(SEPARATOR_SPACINGS),
});

const file = z.strictObject({ type: z.literal("file"), assetId: z.uuid(), spoiler: z.boolean() });

const row = z.strictObject({ type: z.literal("row"), buttons: buttonsSchema });

const CONTENT_COMPONENTS = [textDisplay, section, gallery, separator, file, row] as const;

const containedComponentSchema = z.discriminatedUnion("type", [...CONTENT_COMPONENTS]);

export type ContainedComponent = z.infer<typeof containedComponentSchema>;

const container = z.strictObject({
  type: z.literal("container"),
  accent: hexColor.nullable(),
  spoiler: z.boolean(),
  components: z
    .array(containedComponentSchema)
    .min(1)
    .max(LIMIT.components - 1),
});

const messageComponentSchema = z.discriminatedUnion("type", [...CONTENT_COMPONENTS, container]);

export type MessageComponent = z.infer<typeof messageComponentSchema>;

const componentCount = (component: MessageComponent): number => {
  switch (component.type) {
    case "section":
      return 2 + component.texts.length;
    case "row":
      return 1 + component.buttons.length;
    case "container":
      return 1 + component.components.reduce((sum, inner) => sum + componentCount(inner), 0);
    default:
      return 1;
  }
};

export const templateLeavesRoomForRow = (template: MessageTemplate, buttons: number): boolean => {
  if (typeof template === "string" || template.kind === "plain") return true;
  if (template.kind === "embeds") return template.rows.length < LIMIT.actionRows;
  const used = template.components.reduce((sum, component) => sum + componentCount(component), 0);
  return used + 1 + buttons <= LIMIT.components;
};

const textsOf = (component: MessageComponent): readonly string[] => {
  switch (component.type) {
    case "text":
      return [component.content];
    case "section":
      return component.texts;
    case "container":
      return component.components.flatMap(textsOf);
    default:
      return [];
  }
};

export const templateShowsText = (template: MessageTemplate): boolean => {
  if (typeof template === "string") return showsText(template);
  switch (template.kind) {
    case "plain":
      return showsText(template.content);
    case "embeds":
      return (
        showsText(template.content) ||
        template.embeds.length > 0 ||
        template.rows.length > 0 ||
        template.attachCard === true
      );
    case "components":
      return template.components.flatMap(textsOf).every(showsText);
  }
};

export const reorderingTexts = (template: MessageTemplate): readonly (string | number)[][] => {
  const found: (string | number)[][] = [];
  const walk = (value: unknown, path: readonly (string | number)[]): void => {
    if (typeof value === "string") {
      if (carriesDirectionControl(value)) found.push([...path]);
    } else if (Array.isArray(value)) {
      value.forEach((item: unknown, index) => walk(item, [...path, index]));
    } else if (typeof value === "object" && value !== null) {
      for (const [key, item] of Object.entries(value)) walk(item, [...path, key]);
    }
  };
  walk(template, []);
  return found;
};

const componentTextLength = (components: readonly MessageComponent[]): number =>
  components.flatMap(textsOf).reduce((sum, text) => sum + text.length, 0);

const embedTextLength = (embeds: readonly Embed[]): number =>
  embeds.reduce(
    (sum, embed) =>
      sum +
      embed.title.length +
      embed.description.length +
      embed.author.name.length +
      embed.footer.text.length +
      embed.fields.reduce((inner, field) => inner + field.name.length + field.value.length, 0),
    0,
  );

const attachCard = z.boolean().optional();

const plainMessageSchema = z.strictObject({
  kind: z.literal("plain"),
  content: z.string().min(1).max(MAX_TEMPLATE_LENGTH),
  attachCard,
});

const embedsMessageSchema = z
  .strictObject({
    kind: z.literal("embeds"),
    content: z.string().max(MAX_TEMPLATE_LENGTH),
    embeds: z.array(embedSchema).max(LIMIT.embeds),
    rows: z.array(actionRowSchema).max(LIMIT.actionRows),
    attachCard,
  })
  .refine(
    (message) =>
      message.content !== "" ||
      message.embeds.length > 0 ||
      message.rows.length > 0 ||
      message.attachCard === true,
    refusal("empty_message"),
  )
  .refine(
    (message) => embedTextLength(message.embeds) <= LIMIT.embedTotal,
    refusal("embed_total", ["embeds"]),
  );

const componentsMessageSchema = z
  .strictObject({
    kind: z.literal("components"),
    components: z.array(messageComponentSchema).min(1).max(LIMIT.components),
  })
  .refine(
    (message) =>
      message.components.reduce((sum, component) => sum + componentCount(component), 0) <=
      LIMIT.components,
    refusal("too_many_components", ["components"]),
  )
  .refine(
    (message) => componentTextLength(message.components) <= LIMIT.componentText,
    refusal("component_text", ["components"]),
  );

const messageKindsSchema = z.discriminatedUnion("kind", [
  plainMessageSchema,
  embedsMessageSchema,
  componentsMessageSchema,
]);

export const messageTemplateSchema = z.union([
  z.string().min(1).max(MAX_TEMPLATE_LENGTH),
  messageKindsSchema,
]);

export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

const PLACEHOLDER = /\{(\w+)\}/g;

const clip = (text: string, max: number): string => {
  if (text.length <= max) return text;
  const end = max > 0 && /[\uD800-\uDBFF]/.test(text.charAt(max - 1)) ? max - 1 : max;
  return text.slice(0, end);
};

const MARKUP_VALUES: ReadonlySet<string> = new Set(["user", "host", "winners", "ends", "claimBy"]);

const fill = (
  template: string,
  values: MessageValues,
  escape: (text: string) => string,
  escapeValue: (text: string) => string = escape,
): string =>
  template
    .split(PLACEHOLDER)
    .map((part, index) => {
      if (index % 2 === 0) return escape(part);
      const value = Object.hasOwn(values, part) ? values[part] : undefined;
      if (value === undefined) return escape(`{${part}}`);
      return MARKUP_VALUES.has(part) ? value : escapeValue(value);
    })
    .join("");

const verbatim = (text: string): string => text;

const MARKDOWN_CHARACTERS = /[\\*_~`|[\]()<>#-]/g;

const NUMBERED_LINE = /^(\s*\d+)\./gm;

export const escapeMarkdown = (text: string): string =>
  text
    .replace(MARKDOWN_CHARACTERS, (character) => `\\${character}`)
    .replace(NUMBERED_LINE, "$1\\.");

export const renderMarkdown = (template: string, values: MessageValues): string =>
  clip(fill(template, values, verbatim, escapeMarkdown), LIMIT.content);

export const fillAsTyped = (template: string, values: MessageValues): string =>
  fill(template, values, verbatim);

const renderPlain = (template: string, values: MessageValues): string =>
  clip(fill(template, values, escapeMarkdown), LIMIT.content);

type Path = readonly (string | number)[];

type NamedFile = { readonly assetId: string } | { readonly card: true };

interface NamedPicture {
  readonly file: NamedFile;
  readonly path: Path;
}

const fileKey = (file: NamedFile): string => ("card" in file ? CARD_PICTURE : file.assetId);

const namedPictures = (template: MessageTemplate | null): readonly NamedPicture[] => {
  const named = (url: string, path: Path): readonly NamedPicture[] => {
    if (url === CARD_PICTURE) return [{ file: { card: true }, path }];
    const assetId = storedPictureId(url);
    return assetId === null ? [] : [{ file: { assetId }, path }];
  };
  const inEmbed = (embed: Embed, at: Path): readonly NamedPicture[] => [
    ...named(embed.author.iconUrl, [...at, "author", "iconUrl"]),
    ...named(embed.thumbnailUrl, [...at, "thumbnailUrl"]),
    ...named(embed.imageUrl, [...at, "imageUrl"]),
    ...named(embed.footer.iconUrl, [...at, "footer", "iconUrl"]),
  ];
  const inComponent = (component: MessageComponent, at: Path): readonly NamedPicture[] => {
    switch (component.type) {
      case "file":
        return [{ file: { assetId: component.assetId }, path: [...at, "assetId"] }];
      case "section":
        return component.accessory.type === "thumbnail"
          ? named(component.accessory.url, [...at, "accessory", "url"])
          : [];
      case "gallery":
        return component.items.flatMap((item, index) =>
          named(item.url, [...at, "items", index, "url"]),
        );
      case "container":
        return component.components.flatMap((inner, index) =>
          inComponent(inner, [...at, "components", index]),
        );
      default:
        return [];
    }
  };
  if (template === null || typeof template === "string") return [];
  const attached: readonly NamedPicture[] =
    template.kind !== "components" && template.attachCard === true
      ? [{ file: { card: true }, path: ["attachCard"] }]
      : [];
  switch (template.kind) {
    case "plain":
      return attached;
    case "embeds":
      return [
        ...template.embeds.flatMap((embed, index) => inEmbed(embed, ["embeds", index])),
        ...attached,
      ];
    case "components":
      return template.components.flatMap((component, index) =>
        inComponent(component, ["components", index]),
      );
  }
};

export const templateFiles = (template: MessageTemplate | null): readonly string[] => [
  ...new Set(
    namedPictures(template).flatMap(({ file }) => ("assetId" in file ? [file.assetId] : [])),
  ),
];

export const templateShowsCard = (template: MessageTemplate | null): boolean =>
  namedPictures(template).some(({ file }) => "card" in file);

export const pictureOverLimit = (template: MessageTemplate | null): Path | null => {
  const attached = new Set<string>();
  for (const { file, path } of namedPictures(template)) {
    attached.add(fileKey(file));
    if (attached.size > LIMIT.attachments) return path;
  }
  return null;
};

export const STORED_PICTURE_TYPE = "image/webp";

export const attachmentName = (position: number, type: string): string =>
  `picture-${String(position)}.${type.slice(type.indexOf("/") + 1)}`;

interface DiscordEmbedPayload {
  readonly title?: string;
  readonly description?: string;
  readonly url?: string;
  readonly color?: number;
  readonly timestamp?: string;
  readonly author?: { readonly name: string; readonly url?: string; readonly icon_url?: string };
  readonly fields?: readonly {
    readonly name: string;
    readonly value: string;
    readonly inline: boolean;
  }[];
  readonly thumbnail?: { readonly url: string };
  readonly image?: { readonly url: string };
  readonly footer?: { readonly text: string; readonly icon_url?: string };
}

interface DiscordButtonPayload {
  readonly type: 2;
  readonly style: typeof LINK_BUTTON_STYLE;
  readonly label: string;
  readonly url: string;
}

export const INTERACTIVE_BUTTON_STYLES = {
  primary: 1,
  secondary: 2,
  success: 3,
  danger: 4,
} as const;

type InteractiveButtonStyle =
  (typeof INTERACTIVE_BUTTON_STYLES)[keyof typeof INTERACTIVE_BUTTON_STYLES];

const CUSTOM_ID_LIMIT = 100;

interface DiscordInteractiveButtonPayload {
  readonly type: 2;
  readonly style: InteractiveButtonStyle;
  readonly label: string;
  readonly custom_id: string;
  readonly disabled: boolean;
  readonly emoji?: { readonly id?: string; readonly name: string; readonly animated?: boolean };
}

interface DiscordRowPayload {
  readonly type: 1;
  readonly components: readonly (DiscordButtonPayload | DiscordInteractiveButtonPayload)[];
}

interface DiscordStringSelectPayload {
  readonly type: 3;
  readonly custom_id: string;
  readonly placeholder?: string;
  readonly options: readonly {
    readonly label: string;
    readonly value: string;
    readonly description?: string;
    readonly emoji?: { readonly name: string };
    readonly default?: boolean;
  }[];
}

interface DiscordMenuRowPayload {
  readonly type: 1;
  readonly components: readonly [DiscordStringSelectPayload];
}

interface DiscordMediaPayload {
  readonly media: { readonly url: string };
  readonly description?: string;
  readonly spoiler: boolean;
}

type DiscordThumbnailPayload = { readonly type: 11 } & DiscordMediaPayload;

interface DiscordTextPayload {
  readonly type: 10;
  readonly content: string;
}

interface DiscordSectionPayload {
  readonly type: 9;
  readonly components: readonly DiscordTextPayload[];
  readonly accessory: DiscordThumbnailPayload | DiscordButtonPayload;
}

interface DiscordGalleryPayload {
  readonly type: 12;
  readonly items: readonly DiscordMediaPayload[];
}

interface DiscordSeparatorPayload {
  readonly type: 14;
  readonly divider: boolean;
  readonly spacing: 1 | 2;
}

interface DiscordFilePayload {
  readonly type: 13;
  readonly file: { readonly url: string };
  readonly spoiler: boolean;
}

type DiscordContainedPayload =
  | DiscordTextPayload
  | DiscordSectionPayload
  | DiscordGalleryPayload
  | DiscordSeparatorPayload
  | DiscordFilePayload
  | DiscordRowPayload
  | DiscordMenuRowPayload;

interface DiscordContainerPayload {
  readonly type: 17;
  readonly accent_color?: number;
  readonly spoiler: boolean;
  readonly components: readonly DiscordContainedPayload[];
}

export type DiscordComponentPayload = DiscordContainedPayload | DiscordContainerPayload;

export interface MessagePayload {
  readonly content?: string;
  readonly embeds?: readonly DiscordEmbedPayload[];
  readonly components?: readonly DiscordComponentPayload[];
  readonly flags?: number;
}

export interface InteractiveMessagePayload extends Omit<MessagePayload, "components"> {
  readonly components?: readonly (DiscordComponentPayload | DiscordMenuRowPayload)[];
}

export interface AttachedFile {
  readonly name: string;
}

export interface MessageRendering {
  readonly values: MessageValues;
  readonly timestamp: string;
  readonly files: ReadonlyMap<string, AttachedFile>;
  readonly card: AttachedFile | null;
}

const colorOf = (hex: string): number => Number.parseInt(hex.slice(1), 16);

const attachmentUrl = (file: AttachedFile): string => `attachment://${file.name}`;

const pictureUrl = (url: string, { values, files, card }: MessageRendering): string => {
  if (url === AVATAR_PICTURE) return values.avatar;
  if (url === CARD_PICTURE) return card === null ? "" : attachmentUrl(card);
  const assetId = storedPictureId(url);
  if (assetId === null) return url;
  const attached = files.get(assetId);
  return attached === undefined ? "" : attachmentUrl(attached);
};

const allowance = (total: number, values: MessageValues) => {
  let left = total;
  return (template: string, max: number, markdown = true): string => {
    const escapeValue = markdown ? escapeMarkdown : verbatim;
    const text = clip(fill(template, values, verbatim, escapeValue).trim(), Math.min(max, left));
    left -= text.length;
    return text;
  };
};

const present = <T extends object>(key: keyof T, value: string | undefined): Partial<T> =>
  value === undefined || value === "" ? {} : ({ [key]: value } as Partial<T>);

const buttonPayload = (button: LinkButton, values: MessageValues): DiscordButtonPayload => ({
  type: DISCORD_COMPONENT_TYPES.button,
  style: LINK_BUTTON_STYLE,
  label: clip(fill(button.label, values, verbatim), LIMIT.buttonLabel),
  url: button.url,
});

const rowPayload = (buttons: readonly LinkButton[], values: MessageValues): DiscordRowPayload => ({
  type: DISCORD_COMPONENT_TYPES.row,
  components: buttons.map((button) => buttonPayload(button, values)),
});

const embedPayloads = (
  embeds: readonly Embed[],
  rendering: MessageRendering,
): readonly DiscordEmbedPayload[] => {
  const { values, timestamp } = rendering;
  const take = allowance(LIMIT.embedTotal, values);
  return embeds.flatMap((embed): readonly DiscordEmbedPayload[] => {
    const thumbnail = pictureUrl(embed.thumbnailUrl, rendering);
    const image = pictureUrl(embed.imageUrl, rendering);
    const authorName = take(embed.author.name, LIMIT.authorName, false);
    const title = take(embed.title, LIMIT.embedTitle);
    const description = take(embed.description, LIMIT.embedDescription);
    const fields = embed.fields.flatMap((field) => {
      const name = take(field.name, LIMIT.fieldName);
      const value = take(field.value, LIMIT.fieldValue);
      return name === "" || value === "" ? [] : [{ name, value, inline: field.inline }];
    });
    const footer = take(embed.footer.text, LIMIT.footerText, false);
    const payload: DiscordEmbedPayload = {
      ...present<DiscordEmbedPayload>("title", title),
      ...present<DiscordEmbedPayload>("description", description),
      ...present<DiscordEmbedPayload>("url", title === "" ? "" : embed.url),
      ...(embed.color === null ? {} : { color: colorOf(embed.color) }),
      ...(embed.timestamp ? { timestamp } : {}),
      ...(authorName === ""
        ? {}
        : {
            author: {
              name: authorName,
              ...present<{ url: string }>("url", embed.author.url),
              ...present<{ icon_url: string }>(
                "icon_url",
                pictureUrl(embed.author.iconUrl, rendering),
              ),
            },
          }),
      ...(fields.length === 0 ? {} : { fields }),
      ...(thumbnail === "" ? {} : { thumbnail: { url: thumbnail } }),
      ...(image === "" ? {} : { image: { url: image } }),
      ...(footer === ""
        ? {}
        : {
            footer: {
              text: footer,
              ...present<{ icon_url: string }>(
                "icon_url",
                pictureUrl(embed.footer.iconUrl, rendering),
              ),
            },
          }),
    };
    const shown =
      payload.title !== undefined ||
      payload.description !== undefined ||
      payload.author !== undefined ||
      payload.fields !== undefined ||
      payload.footer !== undefined ||
      payload.thumbnail !== undefined ||
      payload.image !== undefined;
    return shown ? [payload] : [];
  });
};

const mediaPayload = (item: MediaItem, rendering: MessageRendering): DiscordMediaPayload | null => {
  const url = pictureUrl(item.url, rendering);
  return url === ""
    ? null
    : {
        media: { url },
        ...present<DiscordMediaPayload>(
          "description",
          clip(fill(item.description, rendering.values, verbatim), LIMIT.mediaDescription),
        ),
        spoiler: item.spoiler,
      };
};

const componentPayloads = (
  components: readonly MessageComponent[],
  rendering: MessageRendering,
): readonly DiscordComponentPayload[] => {
  const { values, files } = rendering;
  const take = allowance(LIMIT.componentText, values);
  const one = (component: MessageComponent): readonly DiscordComponentPayload[] => {
    switch (component.type) {
      case "text": {
        const content = take(component.content, LIMIT.componentText);
        return content === "" ? [] : [{ type: DISCORD_COMPONENT_TYPES.text, content }];
      }
      case "section": {
        const texts = component.texts.flatMap((text) => {
          const content = take(text, LIMIT.componentText);
          return content === "" ? [] : [{ type: DISCORD_COMPONENT_TYPES.text, content } as const];
        });
        if (texts.length === 0) return [];
        const { accessory } = component;
        if (accessory.type === "button") {
          return [
            {
              type: DISCORD_COMPONENT_TYPES.section,
              components: texts,
              accessory: buttonPayload(accessory, values),
            },
          ];
        }
        const thumbnail = mediaPayload(accessory, rendering);
        return thumbnail === null
          ? texts
          : [
              {
                type: DISCORD_COMPONENT_TYPES.section,
                components: texts,
                accessory: { type: DISCORD_COMPONENT_TYPES.thumbnail, ...thumbnail },
              },
            ];
      }
      case "gallery": {
        const items = component.items.flatMap((item) => {
          const shown = mediaPayload(item, rendering);
          return shown === null ? [] : [shown];
        });
        return items.length === 0 ? [] : [{ type: DISCORD_COMPONENT_TYPES.gallery, items }];
      }
      case "separator":
        return [
          {
            type: DISCORD_COMPONENT_TYPES.separator,
            divider: component.divider,
            spacing: SPACING_VALUE[component.spacing],
          },
        ];
      case "file": {
        const attached = files.get(component.assetId);
        return attached === undefined
          ? []
          : [
              {
                type: DISCORD_COMPONENT_TYPES.file,
                file: { url: attachmentUrl(attached) },
                spoiler: component.spoiler,
              },
            ];
      }
      case "row":
        return [rowPayload(component.buttons, values)];
      case "container": {
        const inner = component.components.flatMap(one) as DiscordContainedPayload[];
        return inner.length === 0
          ? []
          : [
              {
                type: DISCORD_COMPONENT_TYPES.container,
                ...(component.accent === null ? {} : { accent_color: colorOf(component.accent) }),
                spoiler: component.spoiler,
                components: inner,
              },
            ];
      }
    }
  };
  return components.flatMap(one);
};

export const messagePayload = (
  template: MessageTemplate,
  rendering: MessageRendering,
): MessagePayload => {
  if (typeof template === "string") return { content: renderMarkdown(template, rendering.values) };
  switch (template.kind) {
    case "plain":
      return { content: renderPlain(template.content, rendering.values) };
    case "embeds": {
      const content = renderMarkdown(template.content, rendering.values).trim();
      const embeds = embedPayloads(template.embeds, rendering);
      return {
        ...(content === "" ? {} : { content }),
        ...(embeds.length === 0 ? {} : { embeds }),
        ...(template.rows.length === 0
          ? {}
          : { components: template.rows.map((row) => rowPayload(row.buttons, rendering.values)) }),
      };
    }
    case "components":
      return {
        flags: COMPONENTS_V2_FLAG,
        components: componentPayloads(template.components, rendering),
      };
  }
};

export const standardEmojiSchema = z
  .string()
  .min(1)
  .max(16)
  .regex(/^\p{Extended_Pictographic}/u, "is not an emoji");

export const buttonEmojiSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("standard"), emoji: standardEmojiSchema }),
  z.strictObject({
    kind: z.literal("server"),
    id: z.string().regex(DISCORD_SNOWFLAKE, "is not a Discord id"),
    name: z.string().min(1).max(32),
    animated: z.boolean(),
  }),
]);

export type ButtonEmoji = z.infer<typeof buttonEmojiSchema>;

const emojiPayload = (emoji: ButtonEmoji) =>
  emoji.kind === "standard"
    ? { name: emoji.emoji }
    : { id: emoji.id, name: emoji.name, animated: emoji.animated };

export interface InteractiveButton {
  readonly customId: string;
  readonly label: string;
  readonly style: InteractiveButtonStyle;
  readonly disabled: boolean;
  readonly emoji?: ButtonEmoji | undefined;
}

const interactiveRow = (buttons: readonly InteractiveButton[]): DiscordRowPayload => ({
  type: DISCORD_COMPONENT_TYPES.row,
  components: buttons.slice(0, LIMIT.rowButtons).map((button) => {
    if (button.customId.length > CUSTOM_ID_LIMIT) {
      throw new Error(`a custom_id holds at most ${CUSTOM_ID_LIMIT} characters`);
    }
    return {
      type: DISCORD_COMPONENT_TYPES.button,
      style: button.style,
      label: clip(button.label, LIMIT.buttonLabel),
      custom_id: button.customId,
      disabled: button.disabled,
      ...(button.emoji === undefined ? {} : { emoji: emojiPayload(button.emoji) }),
    };
  }),
});

const payloadCount = (component: DiscordComponentPayload): number => {
  switch (component.type) {
    case DISCORD_COMPONENT_TYPES.row:
      return 1 + component.components.length;
    case DISCORD_COMPONENT_TYPES.section:
      return 2 + component.components.length;
    case DISCORD_COMPONENT_TYPES.container:
      return 1 + component.components.reduce((sum, inner) => sum + payloadCount(inner), 0);
    default:
      return 1;
  }
};

export const withButtonRow = (
  payload: MessagePayload,
  buttons: readonly InteractiveButton[],
): MessagePayload => {
  const row = interactiveRow(buttons);
  const kept = [...(payload.components ?? [])];
  if (payload.flags === COMPONENTS_V2_FLAG) {
    const total = (): number =>
      [...kept, row].reduce((sum, component) => sum + payloadCount(component), 0);
    while (kept.length > 0 && total() > LIMIT.components) kept.pop();
  } else {
    kept.splice(LIMIT.actionRows - 1);
  }
  return { ...payload, components: [...kept, row] };
};

export const withButtonsInside = (
  payload: MessagePayload,
  buttons: readonly InteractiveButton[],
): MessagePayload => {
  const components = payload.components ?? [];
  const last = components.at(-1);
  if (payload.flags !== COMPONENTS_V2_FLAG || last?.type !== DISCORD_COMPONENT_TYPES.container) {
    return withButtonRow(payload, buttons);
  }
  const row = interactiveRow(buttons);
  const total = components.reduce((sum, component) => sum + payloadCount(component), 0);
  if (total + payloadCount(row) > LIMIT.components) return withButtonRow(payload, buttons);
  const closing: DiscordContainerPayload = { ...last, components: [...last.components, row] };
  return { ...payload, components: [...components.slice(0, -1), closing] };
};
